import { useQuery } from '@tanstack/react-query';
import {
  CognitoUser,
  ICognitoUserAttributeData,
  UserData,
} from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { captureError } from '../utils/capture-error';

/** The MFA methods Contracting offers, as Cognito names them */
export type MfaMethod = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';

export const MFA_METHODS: MfaMethod[] = [
  'EMAIL_OTP',
  'SMS_MFA',
  'SOFTWARE_TOKEN_MFA',
];

/** True when Cognito's value is one of the methods we offer */
export const isMfaMethod = (value?: string | null): value is MfaMethod =>
  MFA_METHODS.includes(value as MfaMethod);

export interface MfaStatus {
  /** What Cognito uses at sign-in: a method, or 'NOMFA' */
  preferred: string;
  /** Every method switched on for the user (may be more than one) */
  enabled: string[];
  /** email, email_verified, phone_number, phone_number_verified, … */
  attributes: ICognitoUserAttributeData[];
}

/** Invalidate this key after changing a method */
export const MFA_STATUS_KEY = ['Auth.getPreferredMFA'];

/** The value of a Cognito user attribute, if it's set */
export const getUserAttribute = (params: {
  status: MfaStatus | undefined;
  name: string;
}) => params.status?.attributes.find((i) => i.Name === params.name)?.Value;

/**
 * The signed-in user's MFA set-up, shared by the Agent Profile control, the
 * MFA System Screen and the set-up drawer. The preferred method comes from
 * Amplify's getPreferredMFA, as before; the enabled list and attributes come
 * from the same user data, already fetched fresh by the bypassCache call.
 */
export function useMfaStatus() {
  return useQuery({
    queryKey: MFA_STATUS_KEY,
    queryFn: async (): Promise<MfaStatus> => {
      // Bypass Cache to always get the latest preferences for the Current User
      const currentUser: CognitoUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      const preferred = await Auth.getPreferredMFA(currentUser);
      const data = await new Promise<UserData | undefined>(
        (resolve, reject) => {
          currentUser.getUserData((error, result) =>
            error ? reject(error) : resolve(result),
          );
        },
      );

      return {
        preferred,
        enabled: data?.UserMFASettingList || [],
        attributes: data?.UserAttributes || [],
      };
    },
    onError: (error) => captureError({ data: { error } }),
  });
}
